<script>
import {computed} from "vue";
import {useCatalogStore} from "@js/store/CatalogStore.js";
import {checkDocument} from '@js/helpers.ts';

export default {
    name: "CatalogProductSkeleton",
    props: {
        // 'auto' | 'wide' | 'grid'
        variant: {type: String, default: "auto"},
    },
    data() {
        return {isWide: false}
    },
    setup() {
        const catalogStore = useCatalogStore();
        return {catalogStore};
    },
    mounted() {
        this.isWide = computed(() => {
            if (this.variant === "wide") return true;
            if (this.variant === "grid") return false;
            return this.catalogStore?.grid_mode === this.catalogStore?.grid_mods?.wide;
        });
    }
};
</script>

<template>
    <div class="relative w-full rounded-2xl border border-gray-200 bg-white"
         :class="isWide ? 'p-3 md:p-4' : 'p-3'"
         role="status" aria-busy="true">

        <!-- ==================== WIDE ==================== -->
        <template v-if="isWide">
            <!-- Mobile wide skeleton -->
            <div class="md:hidden relative animate-pulse">
                <!-- media + head row -->
                <div class="grid grid-cols-[88px_1fr] gap-3 items-start">
                    <!-- media -->
                    <div class="relative">
                        <div class="w-[88px] h-[88px] rounded-lg border border-gray-200 bg-gray-100"></div>
                        <!-- promo/hit badges -->
                        <div class="absolute left-1 top-1 flex gap-1">
                            <span class="h-4 w-10 rounded bg-gray-300"></span>
                            <span class="h-4 w-8 rounded bg-gray-300"></span>
                        </div>
                    </div>

                    <!-- title + availability + icons -->
                    <div class="min-w-0">
                        <div class="flex items-start justify-between">
                            <div class="h-4 w-20 rounded bg-gray-200"></div>
                            <div class="flex gap-1">
                                <div class="h-8 w-8 rounded-full border border-gray-200 bg-gray-100"></div>
                                <div class="h-8 w-8 rounded-full border border-gray-200 bg-gray-100"></div>
                            </div>
                        </div>

                        <div class="mt-1 h-4 w-4/5 rounded bg-gray-200"></div>
                        <div class="mt-1 h-4 w-3/5 rounded bg-gray-200"></div>

                        <div class="mt-2 flex gap-1.5">
                            <div class="h-6 w-24 rounded bg-gray-200"></div>
                            <div class="h-6 w-20 rounded bg-gray-200"></div>
                        </div>
                    </div>
                </div>

                <!-- unit switch + price -->
                <div class="mt-2 flex items-center justify-between">
                    <div class="inline-flex overflow-hidden rounded-lg border border-gray-200">
                        <div class="h-8 w-20 bg-gray-200"></div>
                        <div class="h-8 w-20 bg-gray-100"></div>
                    </div>
                    <div class="h-5 w-28 rounded bg-gray-200"></div>
                </div>

                <!-- qty + total -->
                <div class="mt-2 flex items-center justify-between">
                    <div class="h-9 w-[104px] rounded-lg border border-gray-200 bg-gray-100"></div>
                    <div class="h-5 w-24 rounded bg-gray-200"></div>
                </div>

                <!-- CTAs -->
                <div class="mt-2 grid grid-cols-2 gap-2">
                    <div class="h-9 w-full rounded-lg bg-gray-200"></div>
                    <div class="h-9 w-full rounded-lg bg-gray-200"></div>
                </div>
            </div>

            <!-- Desktop wide skeleton (как был) -->
            <div class="hidden md:flex flex-col gap-4 md:flex-row md:items-center md:gap-6 animate-pulse relative">
                <!-- media -->
                <div class="relative flex shrink-0 items-center gap-2 w-full md:w-40">
                    <div class="w-full overflow-hidden rounded-xl border border-gray-200">
                        <div class="aspect-[4/3] w-full bg-gray-200"></div>
                    </div>
                    <div class="absolute left-1 top-1 m-1 flex gap-1">
                        <span class="h-4 w-12 rounded bg-gray-300"></span>
                        <span class="h-4 w-9 rounded bg-gray-300"></span>
                    </div>
                </div>

                <!-- center -->
                <div class="flex min-w-0 grow flex-col gap-2">
                    <div class="h-4 w-24 rounded bg-gray-200"></div>
                    <div class="h-4 w-3/4 rounded bg-gray-200"></div>
                    <div class="h-4 w-1/2 rounded bg-gray-200"></div>

                    <div class="mt-1 flex flex-wrap gap-2">
                        <div class="h-6 w-28 rounded bg-gray-200"></div>
                        <div class="h-6 w-24 rounded bg-gray-200"></div>
                    </div>

                    <div class="mt-2 flex flex-wrap items-center gap-3">
                        <div class="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                            <div class="h-8 w-20 bg-gray-200"></div>
                            <div class="h-8 w-20 bg-gray-100"></div>
                        </div>
                        <div class="h-5 w-36 rounded bg-gray-200"></div>
                    </div>
                </div>

                <!-- right column -->
                <div class="flex w-full flex-col items-stretch gap-3 md:w-[360px]">
                    <div class="flex items-center gap-3">
                        <div class="h-10 w-32 rounded-lg border border-gray-200 bg-gray-100"></div>
                        <div class="h-5 w-28 rounded bg-gray-200"></div>
                    </div>
                    <div class="h-11 w-full rounded-lg bg-gray-200"></div>
                </div>

                <div class="absolute end-4 top-4 flex gap-2">
                    <div class="h-9 w-9 rounded-full border border-gray-200 bg-gray-100"></div>
                    <div class="h-9 w-9 rounded-full border border-gray-200 bg-gray-100"></div>
                </div>
            </div>
        </template>

        <!-- ==================== GRID ==================== -->
        <template v-else>
            <div class="relative animate-pulse">
                <div class="absolute right-2 top-2 z-10 flex gap-1">
                    <div class="h-8 w-8 rounded-full border border-gray-200 bg-gray-100"></div>
                    <div class="h-8 w-8 rounded-full border border-gray-200 bg-gray-100"></div>
                </div>

                <div class="rounded-xl overflow-hidden border border-gray-200">
                    <div class="bg-gray-50">
                        <div class="aspect-[4/3] w-full bg-gray-200"></div>
                    </div>
                </div>

                <div class="mt-2 h-4 w-24 rounded bg-gray-200"></div>
                <div class="mt-1 h-4 w-5/6 rounded bg-gray-200"></div>
                <div class="mt-1 h-4 w-2/3 rounded bg-gray-200"></div>

                <div class="mt-2 flex items-center justify-between gap-2">
                    <div class="h-6 w-28 rounded bg-gray-200"></div>
                    <div class="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                        <div class="h-8 w-16 bg-gray-200"></div>
                        <div class="h-8 w-16 bg-gray-100"></div>
                    </div>
                </div>

                <div class="mt-3 flex items-center justify-between gap-2">
                    <div class="h-9 w-28 rounded-lg border border-gray-200 bg-gray-100"></div>
                    <div class="h-5 w-24 rounded bg-gray-200"></div>
                </div>

                <div class="mt-3 flex items-center justify-between gap-2">
                    <div class="h-10 w-full rounded-lg bg-gray-200"></div>
                    <div class="h-10 w-full rounded-lg bg-gray-200"></div>
                </div>
            </div>
        </template>
    </div>
</template>

<style scoped></style>
